
/**
 * Sanitizes a string input to prevent XSS attacks
 * @param input The input string to sanitize
 * @returns A sanitized version of the input
 */
export function sanitizeInput(input: string): string {
  if (!input) return '';
  
  // First encode HTML special characters to prevent XSS
  const htmlEncoded = input
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;');
    
  // Then strip any script-like content that might have been entered
  const scriptStripped = htmlEncoded
    .replace(/<script/gi, '&lt;script')
    .replace(/javascript:/gi, 'disabled-javascript:')
    .replace(/on\w+=/gi, 'data-blocked-event=');
  
  return scriptStripped;
}

/**
 * Validates and sanitizes a URL
 * @param url The URL to validate and sanitize
 * @returns A sanitized URL or null if invalid
 */
export function sanitizeUrl(url: string): string | null {
  if (!url) return null;
  
  try {
    // Validate that it's a proper URL
    const urlObj = new URL(url);
    
    // Only allow http or https protocols
    if (urlObj.protocol !== 'http:' && urlObj.protocol !== 'https:') {
      return null;
    }
    
    // Return the sanitized URL
    return urlObj.toString();
  } catch (e) {
    // If URL parsing fails, it's not a valid URL
    return null;
  }
}

/**
 * Validates a webhook URL specifically for Slack
 * @param url The webhook URL to validate
 * @returns True if valid, false otherwise
 */
export function isValidSlackWebhook(url: string): boolean {
  if (!url) return false;
  
  try {
    const urlObj = new URL(url);
    // Slack webhooks should be from hooks.slack.com domain
    // and use https protocol
    return (
      urlObj.protocol === 'https:' && 
      (urlObj.hostname === 'hooks.slack.com' || 
       urlObj.hostname.endsWith('.hooks.slack.com'))
    );
  } catch (e) {
    return false;
  }
}

/**
 * Validates if input contains potentially dangerous content like script tags
 * @param input The input string to validate
 * @returns True if input is safe, false if potentially dangerous
 */
export function isValidInput(input: string): boolean {
  if (!input) return true;
  
  // Check for script tags, JS events, and other potentially dangerous patterns
  const dangerousPatterns = [
    /<script/i,
    /javascript:/i,
    /on\w+=/i, // onclick, onload, etc.
    /data:text\/html/i,
    /\(\s*\)\s*{/i, // Function pattern like () {
    /new\s+Function/i
  ];
  
  return !dangerousPatterns.some(pattern => pattern.test(input));
}

/**
 * Checks if input contains dangerous content and returns a user-friendly message
 * @param input The input string to validate
 * @returns Object with validation result and message
 */
export function validateUserInput(input: string): { isValid: boolean; message?: string } {
  if (!input) return { isValid: true };
  
  // Define patterns and their corresponding messages
  const validationRules = [
    { pattern: /<script/i, message: "Please avoid using script tags in your input" },
    { pattern: /javascript:/i, message: "Please avoid using 'javascript:' in your input" },
    { pattern: /on\w+=/i, message: "Please avoid using event handlers like 'onclick' in your input" },
    { pattern: /data:text\/html/i, message: "Please avoid using HTML data URLs in your input" },
    { pattern: /\(\s*\)\s*{/i, message: "Please avoid using function definitions in your input" },
    { pattern: /new\s+Function/i, message: "Please avoid using Function constructor in your input" }
  ];
  
  // Check each rule
  for (const rule of validationRules) {
    if (rule.pattern.test(input)) {
      return { isValid: false, message: rule.message };
    }
  }
  
  return { isValid: true };
}

/**
 * Validates a name input to prevent script tags and ensure only valid characters
 * @param name The name to validate
 * @returns True if valid, false otherwise
 */
export function isValidName(name: string): boolean {
  if (!name) return false;
  
  // Check if name contains any script tags or suspicious patterns
  if (!isValidInput(name)) return false;
  
  // Name should only contain letters, spaces, hyphens, and apostrophes
  // No numbers or special characters
  const namePattern = /^[A-Za-zÀ-ÖØ-öø-ÿ\s\-'\.]+$/;
  return namePattern.test(name);
}

/**
 * Strips invalid characters from a name input
 * @param name The name to clean
 * @returns A cleaned version of the name
 */
export function cleanName(name: string): string {
  if (!name) return '';
  
  // Remove any characters that aren't letters, spaces, hyphens, or apostrophes
  return name.replace(/[^A-Za-zÀ-ÖØ-öø-ÿ\s\-'\.]/g, '');
}
